import React from 'react';
import APIEndpointCard from './APIEndpointCard';
import './InputMethodTabs.css';

function InputMethodTabs({
  activeTab,
  onTabChange,
  requirements,
  onRequirementsChange,
  uploadedFile,
  onFileUpload,
  apiEndpoints,
  onEndpointUpdate,
  onEndpointRemove,
  onAddEndpoint,
  maxEndpoints = 5
}) {
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Read file content
      const reader = new FileReader();
      reader.onload = (event) => {
        onRequirementsChange(event.target.result);
        onFileUpload(file);
      };
      reader.readAsText(file);
    }
  };

  const estimateTokens = () => {
    let totalChars = 0;
    
    if (activeTab === 'text') {
      totalChars = requirements.length;
    } else if (activeTab === 'api') {
      apiEndpoints.forEach(endpoint => {
        totalChars += endpoint.url.length;
        totalChars += endpoint.auth.length;
        totalChars += endpoint.headers.length;
        totalChars += endpoint.body.length;
        totalChars += endpoint.expectedResponse.length;
        totalChars += endpoint.description.length;
      });
    }
    
    // Rough estimate: 1 token ≈ 4 characters
    const estimatedTokens = Math.ceil(totalChars / 4);
    return estimatedTokens;
  };

  const tokens = estimateTokens();
  const tokenLimit = 10000;
  const tokenPercentage = (tokens / tokenLimit) * 100;
  const isNearLimit = tokenPercentage > 80;

  return (
    <div className="input-method-tabs">
      {/* Tab Headers */}
      <div className="tabs-header">
        <button
          className={`tab-button ${activeTab === 'text' ? 'active' : ''}`}
          onClick={() => onTabChange('text')}
        >
          📝 Text Requirements
        </button>
        <button
          className={`tab-button ${activeTab === 'api' ? 'active' : ''}`}
          onClick={() => onTabChange('api')}
        >
          🔗 API Specifications
        </button>
        <button
          className={`tab-button ${activeTab === 'file' ? 'active' : ''}`}
          onClick={() => onTabChange('file')}
        >
          📄 File Upload
        </button>
      </div>

      {/* Tab Content */}
      <div className="tabs-content">
        {/* Text Requirements Tab */}
        {activeTab === 'text' && (
          <div className="tab-pane text-tab">
            <label>Describe your requirements:</label>
            <textarea
              className="requirements-textarea-large"
              value={requirements}
              onChange={(e) => onRequirementsChange(e.target.value)}
              placeholder={'Describe your requirements in detail...\n\nExample:\nCreate a REST API for order management with:\n- User authentication\n- Order creation and tracking\n- Payment integration\n- Email notifications\n- Comprehensive validation\n- Unit and integration tests'}
              rows={20}
            />
          </div>
        )}

        {/* API Specifications Tab */}
        {activeTab === 'api' && (
          <div className="tab-pane api-tab">
            <div className="api-endpoints-list">
              {apiEndpoints.map((endpoint, index) => (
                <APIEndpointCard
                  key={index}
                  endpoint={endpoint}
                  index={index}
                  totalEndpoints={apiEndpoints.length}
                  onUpdate={onEndpointUpdate}
                  onRemove={onEndpointRemove}
                />
              ))}
            </div>
            
            <button
              className="btn-add-endpoint"
              onClick={onAddEndpoint}
              disabled={apiEndpoints.length >= maxEndpoints}
            >
              {apiEndpoints.length >= maxEndpoints 
                ? `✓ Maximum ${maxEndpoints} endpoints reached`
                : `+ Add Endpoint (${maxEndpoints - apiEndpoints.length} remaining)`
              }
            </button>
          </div>
        )}

        {/* File Upload Tab */}
        {activeTab === 'file' && (
          <div className="tab-pane file-tab">
            <div className="file-upload-container">
              <input
                type="file"
                id="spec-file-upload"
                accept=".txt,.md,.json,.yaml,.yml,.swagger,.openapi"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="spec-file-upload" className="file-drop-zone">
                <div className="file-icon">📁</div>
                <div className="file-text">
                  {uploadedFile ? (
                    <>
                      <strong>{uploadedFile.name}</strong>
                      <span>Click to change file</span>
                    </>
                  ) : (
                    <>
                      <strong>Drop your specification file here</strong>
                      <span>or click to browse</span>
                    </>
                  )}
                </div>
                <div className="file-formats">
                  Supports: Swagger, OpenAPI, JSON, YAML, TXT, Markdown
                </div>
              </label>
              
              {uploadedFile && requirements && (
                <div className="file-preview-box">
                  <h4>File Preview:</h4>
                  <pre>{requirements.substring(0, 800)}{requirements.length > 800 ? '...' : ''}</pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Token Counter */}
      <div className={`token-counter ${isNearLimit ? 'warning' : ''}`}>
        <span className="token-label">Estimated Tokens:</span>
        <span className="token-value">{tokens.toLocaleString()} / {tokenLimit.toLocaleString()}</span>
        {isNearLimit && <span className="token-warning">⚠️ Approaching limit</span>}
      </div>
    </div>
  );
}

export default InputMethodTabs;
